<template>
  <div>
    <h1>Applications</h1>
    <div v-if="isLoadingList">
      <div class="loader">Loading...</div>
      <p>Loading list of applications...</p>
    </div>
    <div v-else>
      <h5>You have been assigned {{ listAssignments.length }} applications to review. Click on an application to score.</h5>
      <b-progress class="w-50 mb-2" :value="numberOfScored" :max="listAssignments.length" show-value></b-progress>
      <b-button-group class="mb-4">
        <b-button class="jury-btn" :pressed="isNotFiltered" @click="filterAssignments('all')">All</b-button>
        <b-button class="jury-btn" :pressed="isFilteredByScored" @click="filterAssignments('scored')">Scored</b-button>
        <b-button class="jury-btn" :pressed="isFilteredByUnscored" @click="filterAssignments('unscored')">Unscored</b-button>
      </b-button-group>
    </div>
    <b-list-group>
      <b-list-group-item class="application-item" v-for="assignment in listFilteredAssignments" :key="assignment.i" button @click="scoreApp(assignment.student_volunteer_id)">
        <h4>Application {{ assignment.i }}</h4>
        <div class="scores" v-if="assignment.isScored">
          <span>Essay 1 Score: <b-badge variant="primary">{{ assignment.essay1 }}</b-badge></span>
          <span>Essay 2 Score: <b-badge variant="primary">{{ assignment.essay2 }}</b-badge></span>
          <span>Essay 3 Score: <b-badge variant="primary">{{ assignment.essay3 }}</b-badge></span>
          <span>Overall Approval: <b-badge :variant="assignment.overall ? 'success' : 'warning'">{{ assignment.overall ? 'Yes' : 'No' }}</b-badge></span>
          <span>Red flag: <b-badge :variant="assignment.red_flag ? 'danger' : 'success'">{{ assignment.red_flag ? 'Yes' : 'No' }}</b-badge></span>
        </div>
        <div class="scores" v-else>
          <b-badge variant="warning">Not Scored</b-badge>
        </div>
      </b-list-group-item>
    </b-list-group>
    <!-- Modal Component -->
    <b-modal ref="scoreModal" id="scoreModal" :title="getTitle" size="lg" ok-only ok-title="Close" ok-variant="secondary">
      <div v-if="isLoadingApp" class="content">
        <div class="loader">Loading...</div>
      </div>
      <div style="margin:5px" v-else class="content">
        <b-tabs>
          <b-tab title="Essay 1" active>
            <h5 class="essay-question"> Tell us why you would be a great Student Volunteer. </h5>
            <div class="essay-text">
              <p>{{ activeApp.essay1Content }}</p>
            </div>
          </b-tab>
          <b-tab title="Essay 2">
            <h5 class="essay-question"> Tell us about a time when you interacted with individuals from a different background than your own. What did you learn from your experience? </h5>
            <div class="essay-text">
              <p>{{ activeApp.essay2Content }}</p>
            </div>
          </b-tab>
          <b-tab title="Essay 3">
            <h5 class="essay-question">Tell us about a stressful situation involving others that YOU specifically took the initiative to resolve. Explain in detail the technique(s) you used to work towards a resolution. What was the final outcome? How will you take what you learned and apply it to the future?</h5>
            <div class="essay-text">
              <p>{{ activeApp.essay3Content }}</p>
            </div>
          </b-tab>
        </b-tabs>
        <b-form inline @submit.prevent="saveScores">
          <b-form-group class="essay-input" label="Essay 1">
            <b-form-select id="essay1-score" v-model="activeApp.essay1Score" :options="scores" required class="mb-1" />
          </b-form-group>
          <b-form-group class="essay-input" label="Essay 2">
            <b-form-select id="essay2-score" v-model="activeApp.essay2Score" :options="scores" required class="mb-1" />
          </b-form-group>
          <b-form-group class="essay-input" label="Essay 3">
            <b-form-select id="essay3-score" v-model="activeApp.essay3Score" :options="scores" required class="mb-1" />
          </b-form-group>
          <b-form-group class="essay-input" label="Overall Approval">
            <b-form-select id="overall-score" v-model="activeApp.overall" :options="overall" required class="mb-1" />
          </b-form-group>
          <b-form-group class="essay-input" label="Red flag">
            <b-form-select id="red_flag" v-model="activeApp.red_flag" :options="red_flag" required class="mb-1" />
          </b-form-group>
          <b-form-group style="margin-left: 3px" label="Confirm">
            <b-button type="submit" :disabled="isSaving" variant="primary">Save</b-button>
            <div v-if="isSaving" class="loader">Saving...</div>
          </b-form-group>
        </b-form>
        <div v-if="showModalMessage" class="alert alert-success" id="modal-alert" role="alert">
          <p>{{ modalMessage }}</p>
        </div>
      </div>
      <div slot="modal-footer">
        <div class="footer-left">
          <b-button :disabled="isFirstApp" @click="previousApp" variant="info">Previous App</b-button>
          <b-button :disabled="isLastApp" @click="nextApp" variant="info">Next App</b-button>
        </div>
        <div class="footer-right">
          <b-button @click="closeModal" variant="secondary">Close</b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'

  export default {
    name: 'ListJuryApplications',
    data () {
      return {
        isLoadingList: false,
        isLoadingApp: false,
        isSaving: false,
        isNotFiltered: true,
        isFilteredByScored: false,
        isFilteredByUnscored: false,
        scores: [
          { value: null, text: 'Score...' },
          { value: 1, text: '1' },
          { value: 2, text: '2' },
          { value: 3, text: '3' },
          { value: 4, text: '4' },
          { value: 5, text: '5' }
        ],
        overall: [
          { value: null, text: 'Select...' },
          { value: 1, text: 'Yes' },
          { value: 0, text: 'No' }
        ],
        red_flag: [
          { value: 0, text: 'No' },
          { value: 1, text: 'Yes' }
        ],
        showModalMessage: false,
        modalMessage: ''
      }
    },
    computed: {
      ...mapGetters('jury', ['listAssignments']),
      ...mapState('jury', ['assignments', 'activeApp']),
      listFilteredAssignments () {
        if (this.isNotFiltered) {
          return this.listAssignments
        } else {
          if (this.isFilteredByScored) {
            return this.listAssignments.filter(assignment => {
              return assignment.isScored
            })
          } else {
            return this.listAssignments.filter(assignment => {
              return !assignment.isScored
            })
          }
        }
      },
      numberOfScored () {
        return this.listAssignments.filter(assignment => {
          return assignment.isScored
        }).length
      },
      getTitle () {
        if (this.isLoadingApp) {
          return 'Loading...'
        } else {
          return 'Score Application ' + this.activeApp.i
        }
      },
      isFirstApp () {
        return this.activeApp.i === 1
      },
      isLastApp () {
        return this.activeApp.essay1Score === null ||
          this.assignments[this.activeApp.i - 1].essay1 !== this.activeApp.essay1Score ||
          this.activeApp.essay2Score === null ||
          this.assignments[this.activeApp.i - 1].essay2 !== this.activeApp.essay2Score ||
          this.activeApp.essay3Score === null ||
          this.assignments[this.activeApp.i - 1].essay3 !== this.activeApp.essay3Score ||
          this.activeApp.overall === null ||
          this.assignments[this.activeApp.i - 1].overall !== this.activeApp.overall ||
          this.activeApp.red_flag === null ||
          this.assignments[this.activeApp.i - 1].red_flag !== this.activeApp.red_flag ||
          this.activeApp.i === this.assignments.length
      }
    },
    methods: {
      filterAssignments (filter) {
        switch (filter) {
          case 'all':
            this.isNotFiltered = true
            this.isFilteredByScored = false
            this.isFilteredByUnscored = false
            break
          case 'scored':
            this.isNotFiltered = false
            this.isFilteredByScored = true
            this.isFilteredByUnscored = false
            break
          case 'unscored':
            this.isNotFiltered = false
            this.isFilteredByScored = false
            this.isFilteredByUnscored = true
            break
        }
      },
      scoreApp (svId) {
        this.isLoadingApp = true
        this.$refs.scoreModal.show()
        this.$store.dispatch('jury/setActiveApp', svId).then(() => {
          this.isLoadingApp = false
        }).catch(err => {
          this.isLoadingApp = false
          console.log(err)
        })
      },
      saveScores () {
        this.isSaving = true
        this.$store.dispatch('jury/saveScores').then(() => {
          this.isSaving = false
          this.modalMessage = 'Scores saved successfully'
          this.showModalMessage = true
          setTimeout(() => {
            this.showModalMessage = false
            this.modalMessage = ''
          }, 3000)
        })
      },
      previousApp () {
        this.scoreApp(this.assignments[this.activeApp.i - 2].student_volunteer_id)
      },
      nextApp () {
        this.scoreApp(this.assignments[this.activeApp.i].student_volunteer_id)
      },
      closeModal () {
        this.$refs.scoreModal.hide()
      }
    },
    created: function () {
      if (this.$store.state.jury.assignments.length === 0) {
        this.isLoadingList = true
        this.$store.dispatch('jury/getTLAssignments').then(() => {
          this.isLoadingList = false
        }).catch(err => {
          console.log(err)
          this.isLoadingList = false
        })
      }
    }
  }
</script>

<style lang="scss">
  .jury-btn  {
    color: white !important;
    background-color: transparent !important;

    &:focus {
      box-shadow: none !important;
    }
    &:hover:not([disabled="disabled"]) {
      color: transparent !important;
    }
    &:not(:disabled):not(.disabled).active {
      color: transparent !important;
      background-image: linear-gradient(90deg,#E64341, #F6E734, #FFF, #41BDE9,#2C3986);
      -webkit-background-clip: text;
      background-origin: content-box;
      background-repeat: no-repeat;
    }
  }
  .application-item {
    font-size: 1.2em;
  }
  .scores {
    display: inline;
    span {
      margin: 0px 10px;
    }
  }
  .modal-footer {
    display: block !important;
    .footer-left {
      text-align: left;
      display: inline-block;
    }
    .footer-right {
      text-align: right;
      float: right;
    }
  }
  .essay-question, .essay-text {
    text-align: justify;
    margin: 15px 15px 20px 10px;
  }
  .essay-input {
    margin: 5px;
  }
</style>



// WEBPACK FOOTER //
// src/components/jury/ListJuryApplications.vue